import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CubeIcon, MapPinIcon, CheckCircleIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { usePedidos } from '../hooks/usePedidos';
import { useEmpresas } from '../hooks/usePesquisas';
import Toolbar from '../components/pedidos/Toolbar';
import KanbanBoard from '../components/pedidos/KanbanBoard';
import AjusteRegiao from '../components/pedidos/AjusteRegiao';
import PedidoDetalhes from '../components/pedidos/PedidoDetalhes';
import type { PainelRegiao } from '../types/pedidos';

// Contexto do layout principal
interface OutletContextType {
  isFullscreen: boolean;
  setIsFullscreen: (value: boolean) => void;
}

// Componente para o "esqueleto" do card enquanto carrega
const CardSkeleton: React.FC = () => (
  <div className="w-full rounded-iris-lg border border-line bg-surface-2 p-4">
    <div className="flex animate-pulse items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-full bg-line-strong"></div>
        <div className="h-4 w-20 rounded bg-line-strong"></div>
      </div>
      <div className="h-3 w-16 rounded bg-line"></div>
    </div>
    <div className="mt-4 space-y-3 animate-pulse">
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 rounded bg-line-strong"></div>
        <div className="h-3 w-2/3 rounded bg-line"></div>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 rounded bg-line-strong"></div>
        <div className="h-3 w-1/2 rounded bg-line"></div>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 rounded bg-line-strong"></div>
        <div className="h-3 w-3/4 rounded bg-line"></div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="h-6 w-20 rounded-full bg-line-strong"></div>
        <div className="h-6 w-24 rounded-full bg-line-strong"></div>
      </div>
    </div>
  </div>
);

// Componente para o esqueleto da coluna
const ColumnSkeleton: React.FC = () => (
  <div className="w-full min-w-[360px] flex-shrink-0">
    <div className="mb-4 rounded-iris-lg border border-line bg-surface p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between animate-pulse">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-line-strong"></div>
          <div className="h-5 w-24 rounded bg-line-strong"></div>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded bg-line"></div>
          <div className="h-6 w-8 rounded-full bg-line-strong"></div>
        </div>
      </div>
      <div className="space-y-3">
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    </div>
  </div>
);

// Função para deduplificar pedidos
const dedup = (arr: PainelRegiao[]): PainelRegiao[] => {
  const seen = new Set<string>();
  return arr.filter((r) => {
    const k = r?.codigo ?? `${r?.codigocliente}-${r?.data}-${r?.hora}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const PedidosPage: React.FC = () => {
  // Tentar obter o contexto, mas usar valores padrão se não disponível
  const outletContext = useOutletContext<OutletContextType>();
  const isFullscreen = outletContext?.isFullscreen ?? false;
  const setIsFullscreen = outletContext?.setIsFullscreen ?? (() => {});

  const {
    pedidos,
    error,
    loading,
    fetchData,
    filters,
    setFilters,
    autorefresh,
    setAutorefresh,
  } = usePedidos();

  // Carrega empresas e seleciona TODAS por default no primeiro load.
  // Mandar a lista completa pro backend é semanticamente igual a "sem filtro",
  // mas deixa o filtro visualmente preenchido pro usuário ver as opções.
  const { data: empresasData } = useEmpresas();
  const empresas = useMemo(
    () => (empresasData || []).map(e => ({
      codigo: e.codigo,
      label: `${e.codigo} — ${e.nomeFantasia || e.razaosocial || e.codigo}`,
    })),
    [empresasData]
  );

  const empresasInitRef = useRef(false);
  useEffect(() => {
    if (!empresasInitRef.current && empresas.length > 0) {
      empresasInitRef.current = true;
      setFilters.setFEmpresas(empresas.map(e => e.codigo));
    }
  }, [empresas, setFilters]);

  const [editingPedido, setEditingPedido] = useState<PainelRegiao | null>(null);
  const [selectedPedido, setSelectedPedido] = useState<PainelRegiao | null>(null);
  const [layout, setLayout] = useState<string>(() => 
    localStorage.getItem("kanban_layout") || "horizontal"
  );

  // Processar pedidos: deduplificar e filtrar
  const { porRegiao, regioes, todasRegioes, pedidosFiltrados } = useMemo(() => {
    // Primeiro deduplificar
    const pedidosUnicos = dedup(pedidos);
    
    // Extrair TODAS as regiões disponíveis ANTES de aplicar filtros
    const todasRegioesSet = new Set<string>();
    for (const p of pedidosUnicos) {
      const regiao = (p.regiao ?? "Sem Região").toString().trim();
      todasRegioesSet.add(regiao);
    }
    const todasRegioesOrdenadas = Array.from(todasRegioesSet).sort();
    
    // Agora aplicar filtros
    let pedidosFiltradosTemp = [...pedidosUnicos];
    
    // Aplicar filtros de texto
    if (filters.q) {
      const searchText = filters.q.toLowerCase();
      pedidosFiltradosTemp = pedidosFiltradosTemp.filter(p => 
        (p.cliente?.toLowerCase().includes(searchText)) ||
        (p.vendedor?.toLowerCase().includes(searchText)) ||
        (p.cidade?.toLowerCase().includes(searchText)) ||
        (p.endereco?.toLowerCase().includes(searchText))
      );
    }

    // Filtrar por região se especificado (agora com array)
    if (filters.fRegiao && filters.fRegiao.length > 0) {
      pedidosFiltradosTemp = pedidosFiltradosTemp.filter(p => filters.fRegiao.includes(p.regiao || ''));
    }

    // Filtrar por status de conferência se especificado
    if (filters.fConf) {
      pedidosFiltradosTemp = pedidosFiltradosTemp.filter(p => p.conferido?.trim() === filters.fConf);
    }

    // Agrupar por região (apenas dos filtrados para exibição)
    const m: Record<string, PainelRegiao[]> = {};
    for (const p of pedidosFiltradosTemp) {
      const key = (p.regiao ?? "Sem Região").toString().trim();
      (m[key] ??= []).push(p);
    }
    
    const regioesOrdenadas = Object.keys(m).sort();
    
    return { 
      porRegiao: m, 
      regioes: regioesOrdenadas,
      todasRegioes: todasRegioesOrdenadas,
      pedidosFiltrados: pedidosFiltradosTemp
    };
  }, [pedidos, filters.q, filters.fRegiao, filters.fConf]);

  const setLayoutSafe = (v: string) => {
    setLayout(v);
    localStorage.setItem("kanban_layout", v);
  };

  return (
    <div className="flex min-h-screen flex-col overflow-hidden bg-app">
      {/* Page header canônico (UX_AUDIT R1) */}
      {!isFullscreen && (
        <header className="flex items-center gap-2 border-b border-line bg-surface px-3 py-2 md:px-4">
          <CubeIcon className="h-5 w-5 text-accent-text" aria-hidden />
          <div>
            <h1 className="text-base font-semibold text-tx">Painel de Pedidos</h1>
            <p className="text-xs text-tx-2">Acompanhamento Kanban dos pedidos por região e status.</p>
          </div>
        </header>
      )}

      {/* Seção da Toolbar */}
      <div className="sticky top-0 z-10 border-b border-line bg-surface backdrop-blur-xl p-3 md:p-4">
        <Toolbar
          filters={filters}
          setFilters={setFilters}
          regioes={todasRegioes}
          empresas={empresas}
          onRefresh={fetchData}
          layout={layout}
          setLayout={setLayoutSafe}
          autorefresh={autorefresh}
          setAutorefresh={setAutorefresh}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
        />
      </div>

      {/* Seção do Conteúdo (Kanban) */}
      <div className="p-3 md:p-6">
        {error && (
          <div className="mb-6 flex items-center gap-3 rounded-iris-lg border border-danger-border bg-danger-soft p-4 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-danger-soft">
              <CubeIcon className="h-5 w-5 text-danger" />
            </div>
            <div>
              <h4 className="font-semibold text-danger">Erro ao carregar pedidos</h4>
              <p className="text-sm text-danger">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && !isFullscreen && (
          <div className="mb-6 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {/* Total de Pedidos */}
            <div className="group rounded-iris-lg border border-line bg-surface p-4 md:p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl md:text-3xl font-bold text-accent-text">{pedidosFiltrados.length}</div>
                  <div className="text-xs md:text-sm font-medium text-tx-2">Total de Pedidos</div>
                </div>
                <div className="flex h-8 w-8 md:h-12 md:w-12 items-center justify-center rounded-iris bg-accent-soft">
                  <CubeIcon className="h-4 w-4 md:h-6 md:w-6 text-accent-text" />
                </div>
              </div>
            </div>

            {/* Regiões Ativas */}
            <div className="group rounded-iris-lg border border-line bg-surface p-4 md:p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl md:text-3xl font-bold text-ok">{regioes.length}</div>
                  <div className="text-xs md:text-sm font-medium text-tx-2">Regiões Ativas</div>
                </div>
                <div className="flex h-8 w-8 md:h-12 md:w-12 items-center justify-center rounded-iris bg-ok-soft">
                  <MapPinIcon className="h-4 w-4 md:h-6 md:w-6 text-ok" />
                </div>
              </div>
            </div>

            {/* Conferidos */}
            <div className="group rounded-iris-lg border border-line bg-surface p-4 md:p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl md:text-3xl font-bold text-accent-text">
                    {pedidosFiltrados.filter(p => p.conferido?.trim() === 'SIM').length}
                  </div>
                  <div className="text-xs md:text-sm font-medium text-tx-2">Conferidos</div>
                </div>
                <div className="flex h-8 w-8 md:h-12 md:w-12 items-center justify-center rounded-iris bg-accent-soft">
                  <CheckCircleIcon className="h-4 w-4 md:h-6 md:w-6 text-accent-text" />
                </div>
              </div>
            </div>

            {/* Valor Total */}
            <div className="group rounded-iris-lg border border-line bg-surface p-4 md:p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm md:text-3xl font-bold text-warning">
                    R$ {pedidosFiltrados.reduce((acc, p) => acc + (parseFloat(p.valorpedido?.toString() || '0')), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-xs md:text-sm font-medium text-tx-2">Valor Total</div>
                </div>
                <div className="flex h-8 w-8 md:h-12 md:w-12 items-center justify-center rounded-iris bg-warning-soft">
                  <CurrencyDollarIcon className="h-4 w-4 md:h-6 md:w-6 text-warning" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lógica de carregamento */}
        {loading ? (
          // Exibe os esqueletos enquanto os dados estão sendo buscados
          <div className="flex gap-6 overflow-x-auto pb-4">
            <ColumnSkeleton />
            <ColumnSkeleton />
            <ColumnSkeleton />
            <ColumnSkeleton />
          </div>
        ) : (
          // Exibe o KanbanBoard quando os dados estiverem prontos
          <KanbanBoard
            layout={layout}
            regioes={regioes}
            porRegiao={porRegiao}
            onEditRegionClick={(pedido: PainelRegiao) => setEditingPedido(pedido)}
            onPedidoClick={(pedido: PainelRegiao) => setSelectedPedido(pedido)}
            isFullscreen={isFullscreen}
          />
        )}
        
        <AjusteRegiao
          pedido={editingPedido}
          onClose={() => setEditingPedido(null)}
          onSaveSuccess={() => {
            setEditingPedido(null);
            fetchData();
          }}
          isFullscreen={isFullscreen}
        />
        
        <PedidoDetalhes
          pedido={selectedPedido}
          onClose={() => setSelectedPedido(null)}
        />
      </div>
    </div>
  );
};

export default PedidosPage;